import {
	Check,
	CloudOff,
	Download,
	FileText,
	Flame,
	RefreshCw,
	Wrench,
} from "lucide-react";
import { motion } from "motion/react";
import {
	BrowserFrame,
	ExampleTag,
	PhoneFrame,
} from "@/components/blocks/frames/Frames";
import { useI18n } from "@/lib/i18n/use-i18n";
import { cn } from "@/lib/utils";
import {
	PaperSheet,
	StageScene,
	stageBar,
	stageDraw,
	stageItem,
	stagePop,
	stageRise,
	type UseCaseSceneProps,
} from "./StageScene";

/** Planning, survey, certificate, client portal. Server renders the survey. */
const DURATIONS = [3600, 4200, 4200, 3800] as const;

/** Day column (0 = Monday) and row of each visit in the week grid. */
const VISIT_SLOTS = {
	a: { day: 0, row: 0, urgent: false },
	b: { day: 1, row: 1, urgent: false },
	c: { day: 2, row: 0, urgent: true },
	d: { day: 3, row: 1, urgent: false },
	e: { day: 4, row: 0, urgent: false },
} as const;

/** Share of the season's visits done, shown in the client portal (38 / 42). */
const SEASON_PROGRESS = 38 / 42;

/**
 * HVAC maintenance: visits planned from the contracts, a boiler-room survey
 * typed offline, the maintenance certificate generated and signed, and the
 * property manager following their buildings.
 */
export function HvacScene(props: UseCaseSceneProps) {
	const { dict } = useI18n();
	const scene = dict.useCases.items.hvac.scene;
	const tag = dict.common.exampleBadge;
	const fields = Object.entries(scene.survey.fields);

	const steps = [
		<BrowserFrame
			key="planning"
			host={scene.host}
			tag={tag}
			className="w-full"
			contentClassName="p-4"
		>
			<div className="mb-3 flex items-center justify-between gap-3">
				<p className="text-sm font-semibold">{scene.planning.title}</p>
				<span className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
					<span className="h-2 w-3 rounded-sm bg-foreground/80" />
					{scene.planning.fromContract}
					<span className="ml-2 h-2 w-3 rounded-sm border border-signal bg-signal/15" />
					{scene.planning.urgent}
				</span>
			</div>
			<div className="grid h-56 grid-cols-5 gap-1.5">
				{Object.entries(scene.planning.days).map(([key, day], dayIndex) => (
					<div
						key={key}
						className="flex flex-col gap-1.5 rounded-lg bg-muted/60 p-1.5"
					>
						<p className="px-0.5 font-mono text-[9.5px] text-muted-foreground">
							{day}
						</p>
						{Object.entries(scene.planning.visits)
							.filter(
								([visit]) =>
									VISIT_SLOTS[visit as keyof typeof VISIT_SLOTS].day ===
									dayIndex,
							)
							.map(([visit, item]) => {
								const slot = VISIT_SLOTS[visit as keyof typeof VISIT_SLOTS];
								return (
									<motion.div
										key={visit}
										variants={stageRise}
										custom={dayIndex}
										className={cn(
											"rounded-md px-1.5 py-1.5 text-[9px] leading-tight",
											slot.row === 1 && "mt-5",
											slot.urgent
												? "border border-signal bg-signal/15"
												: "bg-foreground text-background",
										)}
									>
										<p className="truncate font-semibold">{item.site}</p>
										<p
											className={cn(
												"mt-0.5 truncate",
												slot.urgent ? "text-foreground/70" : "opacity-70",
											)}
										>
											{item.type}
										</p>
										<p className="mt-1 font-mono text-[8px] opacity-70">
											{item.tech}
										</p>
									</motion.div>
								);
							})}
					</div>
				))}
			</div>
		</BrowserFrame>,

		<PhoneFrame key="survey" className="h-full w-auto max-w-none">
			<div className="flex h-full flex-col gap-2 px-3.5 pt-1 pb-3">
				<ExampleTag className="self-start px-1.5 text-[8px]">{tag}</ExampleTag>
				<div className="flex items-center justify-between gap-2">
					<p className="font-mono text-[10px] whitespace-nowrap text-muted-foreground">
						{scene.survey.title}
					</p>
					<motion.span
						variants={stagePop}
						custom={2.2}
						className="flex items-center gap-1 rounded-full bg-signal/15 px-1.5 py-0.5 text-[8.5px] font-medium text-signal-ink"
					>
						<RefreshCw className="size-2.5" />
						{scene.survey.synced}
					</motion.span>
				</div>
				<p className="flex items-center gap-1.5 text-[12px] leading-tight font-semibold">
					<Flame className="size-3.5 shrink-0 text-signal-ink" />
					{scene.survey.place}
				</p>
				<span className="flex w-fit items-center gap-1 rounded-full border px-1.5 py-0.5 text-[8.5px] text-muted-foreground">
					<CloudOff className="size-2.5" />
					{scene.survey.offline}
				</span>
				<div className="grid grid-cols-2 gap-1.5">
					{fields.map(([key, field], index) => (
						<motion.div
							key={key}
							variants={stageRise}
							custom={index}
							className="rounded-lg border px-2 py-1.5"
						>
							<p className="text-[8px] text-muted-foreground">{field.label}</p>
							<p className="font-mono text-[11px] font-medium">{field.value}</p>
						</motion.div>
					))}
				</div>
				<ul className="space-y-1.5">
					{Object.entries(scene.survey.checks).map(([key, label], index) => (
						<li key={key} className="flex items-center gap-2 text-[10px]">
							<motion.span
								variants={stagePop}
								custom={1.1 + index * 0.25}
								className="flex size-3.5 shrink-0 items-center justify-center rounded-full bg-signal text-signal-foreground"
							>
								<Check className="size-2" strokeWidth={3} />
							</motion.span>
							{label}
						</li>
					))}
				</ul>
				<div className="mt-auto rounded-lg border border-dashed px-2 pt-1 pb-1.5">
					<p className="text-[8px] text-muted-foreground">
						{scene.survey.sign}
					</p>
					<Signature className="h-7 w-24" delay={1.4} />
				</div>
			</div>
		</PhoneFrame>,

		<PaperSheet key="certificate" tag={tag}>
			<div className="flex h-full flex-col p-[7%] text-[9px]">
				<div className="flex items-center gap-2 border-b pb-2.5">
					<span className="flex size-6 items-center justify-center rounded bg-foreground text-background">
						<Wrench className="size-3" />
					</span>
					<div className="min-w-0">
						<p className="truncate text-[11px] font-semibold">
							{scene.certificate.title}
						</p>
						<p className="truncate text-[8px] text-muted-foreground">
							{scene.certificate.boiler}
						</p>
					</div>
				</div>
				<p className="mt-2.5 text-[8.5px] text-muted-foreground">
					{scene.certificate.client}
				</p>
				<dl className="mt-2.5 space-y-1.5">
					{fields.map(([key, field], index) => (
						<motion.div
							key={key}
							variants={stageItem}
							custom={index}
							className="flex items-center justify-between gap-3 border-b border-dashed pb-1.5"
						>
							<dt className="text-muted-foreground">{field.label}</dt>
							<dd className="font-mono font-medium">{field.value}</dd>
						</motion.div>
					))}
				</dl>
				<p className="mt-3 font-semibold">{scene.certificate.checksTitle}</p>
				<ul className="mt-1.5 space-y-1">
					{Object.entries(scene.survey.checks).map(([key, label], index) => (
						<motion.li
							key={key}
							variants={stageItem}
							custom={index + fields.length}
							className="flex items-center gap-1.5"
						>
							<Check className="size-2.5 text-signal-ink" strokeWidth={3} />
							{label}
						</motion.li>
					))}
				</ul>
				<div className="mt-3 space-y-1">
					{["w-full", "w-11/12", "w-3/4"].map((width) => (
						<span
							key={width}
							className={cn(
								"block h-1 rounded-full bg-foreground/[0.08]",
								width,
							)}
						/>
					))}
				</div>
				<div className="mt-auto flex items-end justify-between gap-2">
					<div>
						<Signature className="h-8 w-20" delay={0.7} />
						<p className="text-[8px] text-muted-foreground">
							{scene.certificate.signed}
						</p>
					</div>
					<motion.span
						variants={stagePop}
						custom={1.6}
						className="flex -rotate-6 items-center gap-1 rounded-md border-2 border-signal px-2 py-1 text-[10px] font-semibold text-signal-ink"
					>
						<Check className="size-2.5" strokeWidth={3} />
						{scene.certificate.sent}
					</motion.span>
				</div>
			</div>
		</PaperSheet>,

		<BrowserFrame
			key="portal"
			host={scene.host}
			tag={tag}
			className="w-full"
			contentClassName="p-4 sm:p-5"
		>
			<p className="text-sm font-semibold">{scene.portal.title}</p>
			<p className="text-[10px] text-muted-foreground">{scene.portal.season}</p>
			<div className="mt-4 rounded-lg border p-3">
				<div className="flex items-center justify-between text-[10.5px]">
					<span className="font-medium">{scene.portal.progress}</span>
					<span className="font-mono font-semibold">
						{scene.portal.progressValue}
					</span>
				</div>
				<span className="mt-2 block h-1.5 overflow-hidden rounded-full bg-foreground/[0.07]">
					<motion.span
						variants={stageBar}
						className="block h-full origin-left rounded-full bg-signal"
						style={{ width: `${SEASON_PROGRESS * 100}%` }}
					/>
				</span>
			</div>
			<ul className="mt-3 space-y-2">
				{Object.entries(scene.portal.rows).map(([key, row], index) => (
					<motion.li
						key={key}
						variants={stageItem}
						custom={index + 1}
						className="flex items-center gap-3 rounded-lg border bg-background px-3 py-2 text-[10.5px]"
					>
						<span className="min-w-0 flex-1 truncate font-medium">
							{row.site}
						</span>
						<span
							className={cn(
								"shrink-0 rounded-full px-2 py-0.5 text-[9px] font-medium",
								index < 2
									? "bg-signal/15 text-signal-ink"
									: "bg-foreground/[0.06] text-muted-foreground",
							)}
						>
							{row.status}
						</span>
						{index < 2 ? (
							<span className="hidden shrink-0 items-center gap-1 text-[9px] text-muted-foreground sm:flex">
								<FileText className="size-3" />
								{scene.portal.download}
								<Download className="size-2.5" />
							</span>
						) : null}
					</motion.li>
				))}
			</ul>
		</BrowserFrame>,
	];

	return (
		<StageScene
			{...props}
			labels={Object.values(scene.labels)}
			durations={DURATIONS}
			initial={1}
			render={(step) => steps[step]}
		/>
	);
}

/** A handwritten signature drawing itself. */
function Signature({
	className,
	delay,
}: {
	className?: string;
	delay: number;
}) {
	return (
		<svg
			aria-hidden="true"
			viewBox="0 0 120 40"
			fill="none"
			className={className}
		>
			<motion.path
				d="M4 28 C14 6 22 6 20 24 C18 38 30 30 36 18 C40 10 44 30 52 26 C58 22 60 12 66 16 C72 20 70 30 80 26 C90 22 96 14 116 18"
				className="stroke-foreground"
				strokeWidth={1.6}
				strokeLinecap="round"
				strokeLinejoin="round"
				variants={stageDraw}
				custom={delay}
			/>
		</svg>
	);
}
